/**
 * Shared utilities for detecting Bizy notes edit operations in chat messages.
 *
 * Used by both the global admin widget and the meeting sidebar chat to determine
 * when to render the "Updated 1:1 notes" chip + staged animation vs a regular message.
 */

// Write tools that modify notes content
const NOTES_EDIT_SUFFIXES = ['add_to_meeting_notes', 'replace_meeting_notes', 'undo_meeting_notes'];

/**
 * Text patterns that indicate a response is NOT a successful edit,
 * even if a write tool was invoked (the tool may have failed).
 */
const NOTES_READ_PATTERNS = /^(here\s+are|your\s+(current|1:1|upcoming)|the\s+notes|these\s+are)/i;
const NOTES_ERROR_PATTERNS = /^(I\s+(couldn['\u2019]t|could\s+not|can['\u2019]t|cannot|need|don['\u2019]t|can\s+help.+but)|sorry|unfortunately|no\s+upcoming)/i;
const NOTES_HEDGING_PATTERN = /but\s+I\s+(can['\u2019]t|couldn['\u2019]t|don['\u2019]t|could\s+not|cannot|wasn['\u2019]t|was\s+not)/i;

/** Strip leading markdown formatting (bold, italic) for pattern matching */
function stripMarkdown(text: string): string {
	return text.replace(/^[\s*_~`#]+/, '').trim();
}

/** Check if the first line indicates a failed or read-only outcome */
function isErrorOrRead(content: string): boolean {
	const firstLine = stripMarkdown((content || '').split('\n')[0].trim());
	if (!firstLine) return false;

	if (NOTES_READ_PATTERNS.test(firstLine)) return true;
	if (NOTES_ERROR_PATTERNS.test(firstLine)) return true;
	if (NOTES_HEDGING_PATTERN.test(firstLine)) return true;

	return false;
}

/**
 * True only when the response was actually produced by one of the notes-edit
 * tools — no agent-name fallback. The conductor's `tools_used` includes
 * sub-agent leaf tool names (see `Bizy::V2::Chat#extract_tools_used`), so a
 * Meeting-agent invocation that called `add_to_meeting_notes` shows up
 * directly. A Meeting-agent invocation that called `suggest_people_to_meet`
 * or `explain_meeting_suggestion` (Job 6b/6c) does NOT — preventing the
 * "Updated 1:1 notes" header from leaking onto unrelated Meeting responses
 * (HABITS-1240).
 *
 * The error/read/hedging text patterns are kept as a guard against false
 * positives when a notes tool was invoked but the underlying operation
 * failed (e.g. no upcoming meeting found).
 */
export function isNotesEdit(message: { toolsUsed?: string[]; content: string }): boolean {
	if (!message.toolsUsed?.length) return false;

	const usedWriteTool = message.toolsUsed.some((t) => NOTES_EDIT_SUFFIXES.some((s) => t.endsWith(s)));
	if (!usedWriteTool) return false;

	if (isErrorOrRead(message.content)) return false;

	return true;
}

/** Map tool name suffixes to i18n keys (admin_bizy namespace).
 *  Order matters: specific suffixes must come before generic ones
 *  (e.g., get_meeting_notes before --meeting). */
const TOOL_PROGRESS_KEYS: Array<[string, string]> = [
	// Meeting notes tools + sub-agent
	['get_meeting_notes', 'tool_progress.get_meeting_notes'],
	['add_to_meeting_notes', 'tool_progress.add_to_meeting_notes'],
	['replace_meeting_notes', 'tool_progress.replace_meeting_notes'],
	['undo_meeting_notes', 'tool_progress.undo_meeting_notes'],
	['list_meeting_partners', 'tool_progress.list_meeting_partners'],
	['--meeting', 'tool_progress.meeting'],
	// Meeting history & summary
	['get_meeting_history', 'tool_progress.get_meeting_history'],
	['get_meeting_summary', 'tool_progress.get_meeting_summary'],
	// Recognition tools + sub-agent
	['generate_recognition_suggestion', 'tool_progress.generate_recognition_suggestion'],
	['get_recognition_received', 'tool_progress.get_recognition_received'],
	['get_recognition_given', 'tool_progress.get_recognition_given'],
	['get_recognition_hashtags', 'tool_progress.get_recognition_hashtags'],
	['recognition', 'tool_progress.recognition'],
	// Check-ins, milestones & balance
	['get_checkin_history', 'tool_progress.get_checkin_history'],
	['get_user_milestones', 'tool_progress.get_user_milestones'],
	['get_points_balance', 'tool_progress.get_points_balance'],
	['get_redemption_history', 'tool_progress.get_redemption_history'],
	// Org context sub-agent
	['org_context', 'tool_progress.org_context'],
	// Data agent
	['--data--agent', 'tool_progress.data_agent'],
	// Admin tools
	['get_participation_report_data', 'tool_progress.get_participation_report_data'],
	['search_documentation', 'tool_progress.search_documentation'],
];

/**
 * Returns the i18n key for a tool progress label.
 * The caller should resolve it with `t(key)` from useTranslation('admin_bizy').
 */
export function getToolProgressKey(toolName: string | null): string | null {
	if (!toolName) return null;
	const match = TOOL_PROGRESS_KEYS.find(([suffix]) => toolName.endsWith(suffix));
	return match ? match[1] : null;
}
